import React, { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { 
  checkServerStatus, 
  loadAvailableWorkflows, 
  uploadImage, 
  executeWorkflow, 
  checkWorkflowStatus, 
  fetchWorkflowResults 
} from '../services/itoraApi';
import { randomizeWorkflow, updateWorkflowParameter } from '../utils/workflowUtils';
import { isOutputNode, isInputNode } from '../utils/nodeUtils';
import ImageArea from '../components/ImageArea';
import WorkflowControls from '../components/WorkflowControls';
import ParameterEditor from '../components/ParameterEditor';
import LogDisplay from '../components/LogDisplay';

import ImageCropper from '../components/ImageCropper';
import ImageCompare from '../components/ImageCompare';
import './Gen.css';

const Gen = () => {
  // State management
  const [serverStatus, setServerStatus] = useState('checking');
  const [workflow, setWorkflow] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState('Ready');
  const [progressPercentage, setProgressPercentage] = useState(0);
  // Track multiple workflow executions
  const [activeWorkflows, setActiveWorkflows] = useState([]);
  const [logs, setLogs] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [workflowInfo, setWorkflowInfo] = useState('');
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showWebsocketLog, setShowWebsocketLog] = useState(false);
  const [showLogPanel, setShowLogPanel] = useState(false);
  const [showActiveJobs, setShowActiveJobs] = useState(true);
  const [debugImages, setDebugImages] = useState([]);
  const [debugImageData, setDebugImageData] = useState([]);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugPrompt, setDebugPrompt] = useState('');
  const [beforeAfterMode, setBeforeAfterMode] = useState(false);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const handleFullscreenToggle = () => {
    // Make the image compare component fullscreen
    if (results.length > 0) {
      const container = document.querySelector('.compare-mode-container');
      console.log('Fullscreen toggle - container found:', !!container);
      console.log('Fullscreen toggle - current fullscreen element:', document.fullscreenElement);
      
      if (!container) {
        console.log('Fullscreen toggle - no container found');
        return;
      }

      if (!document.fullscreenElement) {
        console.log('Fullscreen toggle - entering fullscreen');
        if (container.requestFullscreen) {
          container.requestFullscreen().catch(err => {
            console.log('Fullscreen error:', err);
          });
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        } else if (container.msRequestFullscreen) {
          container.msRequestFullscreen();
        }
      } else {
        console.log('Fullscreen toggle - exiting fullscreen');
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    }
  };

  useEffect(() => {
    function fullscreenChangeHandler() {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    }
    
    document.addEventListener('fullscreenchange', fullscreenChangeHandler);
    document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler);
    document.addEventListener('mozfullscreenchange', fullscreenChangeHandler);
    document.addEventListener('MSFullscreenChange', fullscreenChangeHandler);
    
    return () => {
      document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('webkitfullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('mozfullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('MSFullscreenChange', fullscreenChangeHandler);
    };
  }, []);
  const [imageHistory, setImageHistory] = useState([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  
  // Parameter editor state
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [parameterValue, setParameterValue] = useState('');
  
  const [executionProgress, setExecutionProgress] = useState({
    currentStep: 0,
    totalSteps: 0,
    currentNode: null,
    rate: null,
    percentage: 0
  });
  
  // Download counter for consistent naming
  const [downloadCounter, setDownloadCounter] = useState(1);

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message }]);
  };

  // WebSocket message handlers
  const handleWebSocketMessage = useCallback((message) => {
    console.log('WebSocket message received:', message);
    
    switch (message.type) {
      case 'execution_start':
        addLog(`Execution started for prompt: ${message.data.prompt_id}`);
        // Find the workflow with this prompt ID and update its status
        setActiveWorkflows(prev => prev.map(wf => 
          wf.promptId === message.data.prompt_id 
            ? { ...wf, status: 'running', progress: 'Starting execution...', progressPercentage: 0 }
            : wf
        ));
        setExecutionProgress({
          currentStep: 0,
          totalSteps: 0,
          currentNode: null,
          rate: null,
          percentage: 0
        });
        updateGlobalProgress();
        break;
        
      case 'execution_cached':
        addLog(`Execution cached for prompt: ${message.data.prompt_id}`);
        setProgress('Using cached result...');
        setProgressPercentage(100);
        break;
        
      case 'executing':
        const { node, prompt_id, progress: execProgress } = message.data;
        addLog(`Executing node: ${node}`);
        
        setExecutionProgress(prev => ({
          ...prev,
          currentNode: node
        }));
        
        if (execProgress) {
          let progressText = `Executing: ${node}`;
          
          if (execProgress.value !== undefined && execProgress.max !== undefined) {
            const percentage = Math.round((execProgress.value / execProgress.max) * 100);
            progressText += ` | ${percentage}% (${execProgress.value}/${execProgress.max})`;
            
            // Update the specific workflow's progress
            setActiveWorkflows(prev => prev.map(wf => 
              wf.promptId === prompt_id 
                ? { ...wf, progress: progressText, progressPercentage: percentage }
                : wf
            ));
            
            setExecutionProgress(prev => ({
              ...prev,
              currentStep: execProgress.value,
              totalSteps: execProgress.max,
              percentage: percentage
            }));
          }
          
          if (execProgress.rate) {
            progressText += ` | ${execProgress.rate} it/s`;
            setExecutionProgress(prev => ({
              ...prev,
              rate: execProgress.rate
            }));
          }
          
          updateGlobalProgress();
        }
        break;
        
      case 'executed':
        addLog(`Node executed: ${message.data.node}`);
        break;
        
      case 'execution_error':
        addLog(`Execution error: ${message.data.error}`);
        setProgress('Execution failed');
        setIsRunning(false);
        break;
        
      case 'progress':
        if (message.data.value !== undefined && message.data.max !== undefined) {
          const percentage = Math.round((message.data.value / message.data.max) * 100);
          setProgressPercentage(percentage);
          setProgress(`Progress: ${percentage}% (${message.data.value}/${message.data.max})`);
        }
        break;
        
      case 'status':
        // Ignore unknown status messages
        if (message.data.status === 'unknown') {
          break;
        }
        if (message.data.exec_info && message.data.exec_info.queue_remaining !== undefined) {
          const remaining = message.data.exec_info.queue_remaining;
          if (remaining === 0) {
            setProgress('Complete');
            setIsRunning(false);
          }
        }
        break;
        
      default:
        // Only log non-crystools.monitor messages
        if (!message.type || !message.type.includes('crystools.monitor')) {
          console.log('Unknown WebSocket message:', message);
        }
        break;
    }
  }, []);

  const handleStatusMessage = useCallback((status) => {
    // This is for WebSocket status changes, not server status
    // Server status is handled separately in checkServerStatusAndLoadWorkflows
    console.log('WebSocket status changed:', status);
  }, []);

  // WebSocket hook - moved after handler functions
  const {
    isConnected: wsConnected,
    logs: websocketLogs,
    currentPromptId,
    hasProgress: hasWebSocketProgress,
    connectionStatus: wsConnectionStatus,
    queueInfo,
    sendMessage, 
    requestQueueInfo,
    disconnect: wsDisconnect, 
    reconnect: wsReconnect 
  } = useWebSocket(handleWebSocketMessage, handleStatusMessage);

  // Monitor workflow state changes
  useEffect(() => {
    console.log('=== STATE CHANGE DEBUG ===');
    console.log('Workflow state changed:', workflow);
    console.log('Selected workflow:', selectedWorkflow);
    console.log('Available workflows:', availableWorkflows);
    console.log('Selected file:', selectedFile);
    console.log('Before image:', beforeImage);
  }, [workflow, selectedWorkflow, availableWorkflows, selectedFile, beforeImage]);

  // Update progress based on WebSocket queue information
  useEffect(() => {
    if (queueInfo.queue_remaining > 0) {
      setProgressPercentage(0);
    } else if (hasWebSocketProgress) {
      // Keep existing progress if WebSocket has provided it
      // Progress will be updated by handleWebSocketMessage
    } else if (isRunning) {
      setProgress('Processing...');
      setProgressPercentage(50);
    }
  }, [queueInfo.queue_remaining, hasWebSocketProgress, isRunning]);

  // Request queue information when WebSocket connects
  useEffect(() => {
    if (wsConnectionStatus === 'comfyui_connected' && requestQueueInfo) {
      // Request queue information after a short delay to ensure connection is stable
      setTimeout(() => {
        requestQueueInfo();
      }, 1000);
    }
  }, [wsConnectionStatus, requestQueueInfo]);

  // Add test function for debugging queue display
  useEffect(() => {
    window.testSetQueue = (queueData) => {
      console.log('Test: Setting queue data:', queueData);
      // This would normally come from the WebSocket hook
      // For testing, we'll manually update the queue info
      if (queueData && queueData.queue_remaining !== undefined) {
        // We need to access the setQueueInfo function from the WebSocket hook
        // For now, let's just log it
        console.log('Test queue data received:', queueData);
      }
    };
    
    return () => {
      delete window.testSetQueue;
    };
  }, []);

  // Initialize component
  useEffect(() => {
    console.log('Gen component mounted, initializing...');
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    await checkServerStatusAndLoadWorkflows();
  };

  const checkServerStatusAndLoadWorkflows = async () => {
    try {
      console.log('Checking server status...');
      const status = await checkServerStatus();
      console.log('Server status response:', status);
      setServerStatus(status.status);
      
      // Always try to load workflows, regardless of ComfyUI server status
      console.log('Loading workflows...');
      const workflows = await loadAvailableWorkflows();
      console.log('Available workflows:', workflows);
      console.log('First workflow:', workflows[0]);
      setAvailableWorkflows(workflows);
      addLog(`Loaded ${workflows.length} available tools`);
      
      if (workflows.length > 0) {
        console.log('Selecting first workflow:', workflows[0]);
        handleWorkflowSelect(workflows[0]);
      } else {
        console.log('No workflows available');
        addLog('No workflows available');
      }
      
      if (status.status !== 'connected') {
        console.log('ComfyUI server not connected:', status.error);
        addLog(`ComfyUI server not connected: ${status.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error initializing:', error);
      addLog(`Error initializing: ${error.message}`);
      setServerStatus('error');
      
      // Still try to load workflows even if there's an error
      try {
        const workflows = await loadAvailableWorkflows();
        console.log('Error fallback - Available workflows:', workflows);
        console.log('Error fallback - First workflow:', workflows[0]);
        setAvailableWorkflows(workflows);
        addLog(`Loaded ${workflows.length} available tools (despite server error)`);
        
        if (workflows.length > 0) {
          console.log('Error fallback - Selecting first workflow:', workflows[0]);
          handleWorkflowSelect(workflows[0]);
        } else {
          console.log('Error fallback - No workflows available');
          addLog('No workflows available (despite server error)');
        }
      } catch (workflowError) {
        addLog(`Failed to load workflows: ${workflowError.message}`);
      }
    }
  };

  const handleWorkflowSelect = async (workflowName) => {
    try {
      console.log('handleWorkflowSelect called with:', workflowName);
      
      // Check if workflowName is valid
      if (!workflowName || workflowName.trim() === '') {
        console.error('Invalid workflow name:', workflowName);
        addLog(`Error: Invalid workflow name: ${workflowName}`);
        return;
      }
      
      setSelectedWorkflow(workflowName);
      addLog(`Loading tool: ${workflowName}`);
      
      // Load the workflow file from the server endpoint
      const url = `http://localhost:3003/itoraWorkflow/${workflowName}`;
      console.log('Fetching workflow from:', url);
      const response = await fetch(url);
      console.log('Workflow response status:', response.status);
      
      if (response.ok) {
        const workflowData = await response.json();
        console.log('Workflow data loaded:', workflowData);
        setWorkflow(workflowData);
        
        // Generate workflow info
        const nodeCount = Object.keys(workflowData).length;
        const outputNodes = Object.values(workflowData).filter(node => isOutputNode(node)).length;
        const inputNodes = Object.values(workflowData).filter(node => isInputNode(node)).length;
        
        setWorkflowInfo(`Nodes: ${nodeCount} | Outputs: ${outputNodes} | Inputs: ${inputNodes}`);
        addLog(`Tool loaded: ${nodeCount} nodes`);
        console.log('Workflow info set:', `Nodes: ${nodeCount} | Outputs: ${outputNodes} | Inputs: ${inputNodes}`);
      } else {
        const errorText = await response.text();
        console.error('Workflow response error:', errorText);
        throw new Error(`Failed to load tool file: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error in handleWorkflowSelect:', error);
      addLog(`Error loading tool: ${error.message}`);
    }
  };

  const handleFileSelect = (file) => {
    console.log('=== FILE SELECT DEBUG ===');
    console.log('handleFileSelect called with:', file);
    console.log('File name:', file.name);
    console.log('File size:', file.size);
    console.log('File type:', file.type);
    
    // Directly set the file without showing cropper
    setSelectedFile(file);
    setBeforeImage(file);
    setResults([]);
    setBeforeAfterMode(false);
    addLog(`Selected file: ${file.name}`);
    
    console.log('File selection complete');
  };

  const handleCropComplete = (croppedFile) => {
    setSelectedFile(croppedFile);
    setBeforeImage(croppedFile);
    setResults([]);
    setBeforeAfterMode(false);
    setShowCropper(false);
    setImageToCrop(null);
    addLog(`Cropped and selected file: ${croppedFile.name}`);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
  };

  const runWorkflow = async () => {
    console.log('=== RUN WORKFLOW DEBUG ===');
    console.log('runWorkflow called');
    console.log('workflow state:', workflow);
    console.log('selectedFile state:', selectedFile);
    console.log('selectedWorkflow state:', selectedWorkflow);
    console.log('workflow keys:', workflow ? Object.keys(workflow) : 'null');
    console.log('selectedFile name:', selectedFile ? selectedFile.name : 'null');
    
    if (!workflow) {
      addLog('No tool selected');
      return;
    }

    console.log('Tool is present, proceeding...');

    try {
      // Create a unique ID for this workflow execution
      const executionId = Date.now().toString();
      const workflowName = selectedWorkflow;
      
      // Add to active workflows and update global progress
      setActiveWorkflows(prev => {
        if (prev.length === 0) {
          setProgress('Adding to queue...');
          setProgressPercentage(0);
        }
        return [...prev, {
          id: executionId,
          name: workflowName,
          status: 'queued',
          progress: 'Adding to queue...',
          progressPercentage: 0
        }];
      });
      
      // Randomize workflow
      const randomizedWorkflow = randomizeWorkflow(workflow);
      
      let updatedWorkflow = randomizedWorkflow;
      
      // Upload image if one is selected
      if (selectedFile) {
        addLog('Uploading input images...');
        const uploadResult = await uploadImage(selectedFile, 'LoadImage');
        
        if (uploadResult.name) {
          addLog(`Upload successful: ${uploadResult.name}`);
          
          
          
          // Find the LOAD node in the workflow - this is where the image gets uploaded
          let loadNodeId = Object.keys(randomizedWorkflow).find(nodeId => 
            randomizedWorkflow[nodeId]._meta?.title === 'LOAD'
          );
          
          if (loadNodeId) {
            addLog(`Found LOAD node: ${loadNodeId}`);
            const loadNode = randomizedWorkflow[loadNodeId];
            addLog(`LOAD node class: ${loadNode.class_type}`);
            
            // Debug: Log the current image parameter value
            if (loadNode.inputs && loadNode.inputs.image) {
              addLog(`Current LOAD node image parameter: ${loadNode.inputs.image}`);
            }
            
            // Update workflow with uploaded image
            updatedWorkflow = updateWorkflowParameter(
              randomizedWorkflow, 
              loadNodeId, 
              'image', 
              uploadResult.name
            );
            
            // Verify the update
            const updatedNode = updatedWorkflow[loadNodeId];
            if (updatedNode.inputs && updatedNode.inputs.image) {
              addLog(`Updated LOAD node image parameter to: ${updatedNode.inputs.image}`);
            }
          } else {
            addLog('No LOAD node found in workflow - available nodes:');
            Object.entries(randomizedWorkflow).forEach(([nodeId, node]) => {
              addLog(`  - Node ${nodeId}: ${node.class_type} (meta: ${node._meta?.title || 'none'})`);
            });
            throw new Error('No LOAD node found in workflow');
          }
        } else {
          throw new Error('Upload failed');
        }
      } else {
        addLog('No image selected, running tool without image input...');
      }
      
      // Execute workflow
      addLog('Executing workflow...');
      
      
      
      const result = await executeWorkflow(updatedWorkflow);
        
      if (result.prompt_id) {
        addLog(`Workflow started with prompt ID: ${result.prompt_id}`);
        
        // Send subscription message to WebSocket
        if (sendMessage) {
          sendMessage({ type: 'subscribe' });
        }
        
        // Update the workflow status
        setActiveWorkflows(prev => prev.map(wf => 
          wf.id === executionId 
            ? { ...wf, status: 'running', promptId: result.prompt_id, progress: 'Processing...' }
            : wf
        ));
        
        // Set global running state
        setIsRunning(true);
        setProgress('Processing...');
        
        // Start monitoring this workflow (don't await - let it run in background)
        monitorWorkflow(executionId, result.prompt_id);
      } else {
        throw new Error('No prompt ID returned');
      }
    } catch (error) {
      addLog(`Error executing workflow: ${error.message}`);
      
      // Update the workflow status to failed and global state
      setActiveWorkflows(prev => {
        const updated = prev.map(wf => 
          wf.id === executionId 
            ? { ...wf, status: 'failed', progress: 'Execution failed' }
            : wf
        );
        
        // Update global state if this was the only workflow
        if (updated.length === 1 && updated[0].status === 'failed') {
          setProgress('Execution failed');
          setIsRunning(false);
        }
        
        return updated;
      });
    }
  };

  const monitorWorkflow = async (executionId, promptId) => {
    while (true) {
      try {
        const status = await checkWorkflowStatus(promptId);
        
        if (status.status === 'completed') {
          addLog(`Workflow ${executionId} completed successfully`);
          
          // Update workflow status
          setActiveWorkflows(prev => prev.map(wf => 
            wf.id === executionId 
              ? { ...wf, status: 'completed', progress: 'Processing images...', progressPercentage: 90 }
              : wf
          ));
          
          // Add a delay to ensure images are fully processed
          addLog('Waiting for images to be ready...');
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Fetch results for this specific workflow
          await fetchResultsForWorkflow(executionId, promptId);
          
          // Update workflow status to complete
          setActiveWorkflows(prev => prev.map(wf => 
            wf.id === executionId 
              ? { ...wf, status: 'complete', progress: 'Complete', progressPercentage: 100 }
              : wf
          ));
          
          // Update global state
          updateGlobalProgress();
          return;
        } else if (status.status === 'failed') {
          addLog(`Workflow ${executionId} failed`);
          setActiveWorkflows(prev => prev.map(wf => 
            wf.id === executionId 
              ? { ...wf, status: 'failed', progress: 'Workflow failed' }
              : wf
          ));
          updateGlobalProgress();
          return;
        } else if (status.status === 'unknown') {
          // Ignore unknown status - don't show it in progress
          console.log('Ignoring unknown status');
        } else if (!hasWebSocketProgress) {
          // Only show other unknown statuses if WebSocket hasn't provided progress
          setActiveWorkflows(prev => prev.map(wf => 
            wf.id === executionId 
              ? { ...wf, progress: 'Status unknown - checking again...' }
              : wf
          ));
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        addLog(`Status check error for workflow ${executionId}: ${error.message}`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  };

  const waitForCompletion = async (promptId) => {
    while (true) {
      try {
        const status = await checkWorkflowStatus(promptId);
        
        if (status.status === 'completed') {
          addLog('Workflow completed successfully');
          setProgress('Processing images...');
          setProgressPercentage(90);
          // Keep isRunning true until images are fully loaded
          
          // Add a delay to ensure images are fully processed
          addLog('Waiting for images to be ready...');
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Fetch results
          await fetchResults(promptId);
          
          // Only now set as complete
          setProgress('Complete');
          setProgressPercentage(100);
          setIsRunning(false);
          return;
        } else if (status.status === 'failed') {
          addLog('Workflow failed');
          setProgress('Workflow failed');
          setIsRunning(false);
          return;
        } else if (status.status === 'unknown') {
          // Ignore unknown status - don't show it in progress
          console.log('Ignoring unknown status');
        } else if (!hasWebSocketProgress) {
          // Only show other unknown statuses if WebSocket hasn't provided progress
          setProgress('Status unknown - checking again...');
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        addLog(`Status check error: ${error.message}`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  };

    const fetchResults = async (promptId) => {
    try {
      setProgress('Loading results...');
      setProgressPercentage(95);
      
      const response = await fetchWorkflowResults(promptId);
      
      console.log('=== FETCH RESULTS DEBUG ===');
      console.log('Response:', response);
      
      // Handle the new response format: { images, outputs }
      const results = response.images || response;
      
      if (results && results.length > 0) {
        setProgress('Processing images...');
        setProgressPercentage(98);
        
        setResults(results);
        
        console.log('=== RESULTS DEBUG ===');
        console.log('Results length:', results.length);
        console.log('Selected file:', selectedFile);
        console.log('Results array:', results);
        
        // Log detailed information about each result
        console.log('=== DETAILED RESULTS ANALYSIS ===');
        results.forEach((result, index) => {
          console.log(`Result ${index}:`, {
            filename: result.filename,
            type: result.type,
            subfolder: result.subfolder,
            hasData: !!result.data,
            dataLength: result.data ? result.data.length : 0,
            allKeys: Object.keys(result),
            fullResult: result
          });
          
          // Check if this is the INPUT image
          if (result.filename && result.filename.includes('INPUT')) {
            console.log(`*** FOUND INPUT IMAGE at index ${index}: ${result.filename} ***`);
          }
          
          // Also check for any other interesting patterns
          if (result.filename && (result.filename.includes('temp') || result.filename.includes('ComfyUI'))) {
            console.log(`*** TEMP IMAGE at index ${index}: ${result.filename} ***`);
          }
        });
        

        
        // Store debug info for display
        const debugInfo = [];
        if (selectedFile) {
          debugInfo.push(`1. UPLOADED FILE: ${selectedFile.name}`);
        }
        results.forEach((result, index) => {
          const resultDetails = [
            `Result ${index + 1} [${index}]`,
            `Filename: ${result.filename}`,
            `Type: ${result.type || 'unknown'}`,
            `Subfolder: ${result.subfolder || 'none'}`,
            `Has Data: ${result.data ? 'Yes' : 'No'}`,
            `Data Length: ${result.data ? result.data.length : 0}`,
            `All Keys: ${Object.keys(result).join(', ')}`
          ].join(' | ');
          
          debugInfo.push(`${index + (selectedFile ? 2 : 1)}. ${resultDetails}`);
        });
        console.log('=== DEBUG INFO BEING SET ===');
        console.log('debugInfo array:', debugInfo);
        console.log('debugInfo length:', debugInfo.length);
        setDebugImages(debugInfo);
        
        // Store the actual images for visual display
        const debugImageData = [];
        
        // Add uploaded file if any
        if (selectedFile) {
          debugImageData.push({
            type: 'uploaded',
            name: selectedFile.name,
            image: URL.createObjectURL(selectedFile),
            info: `1. UPLOADED FILE: ${selectedFile.name}`,
            nodeType: 'uploaded'
          });
        }
        
        // Map results to INPUT/OUTPUT nodes using proper node mapping
        const { inputResult: mappedInputResult, outputResult: mappedOutputResult } = mapResultsToNodes(workflow, results);
        
        // Also try to get additional information from the workflow status and history
        try {
          const statusResponse = await checkWorkflowStatus(promptId);
          console.log('=== WORKFLOW STATUS DEBUG ===');
          console.log('Status response:', statusResponse);
          
          // Try to get workflow history for additional mapping info
          try {
            const historyResponse = await fetchWorkflowHistory(promptId);
            console.log('=== WORKFLOW HISTORY DEBUG ===');
            console.log('History response:', historyResponse);
          } catch (historyError) {
            console.log('Could not fetch workflow history:', historyError);
          }
        } catch (error) {
          console.log('Could not fetch workflow status:', error);
        }
        
        results.forEach((result, index) => {
          let nodeType = 'other';
          let nodeInfo = '';
          
          // Determine node type based on mapping
          if (result === mappedInputResult) {
            nodeType = 'INPUT';
            nodeInfo = ' (INPUT node)';
          } else if (result === mappedOutputResult) {
            nodeType = 'OUTPUT';
            nodeInfo = ' (OUTPUT node)';
          } else {
            // Check if this result corresponds to a LOAD node
            const loadNodes = [];
            Object.entries(workflow).forEach(([nodeId, node]) => {
              if (node._meta && node._meta.title === 'LOAD') {
                loadNodes.push({ nodeId, node });
              }
            });
            
            if (loadNodes.length > 0) {
              const nodeIds = Object.keys(workflow);
              const loadNodeIndex = nodeIds.indexOf(loadNodes[0].nodeId);
              if (index === loadNodeIndex) {
                nodeType = 'LOAD';
                nodeInfo = ' (LOAD node)';
              }
            }
          }
          
          const imageInfo = {
            type: 'workflow',
            name: result.filename,
            info: `${index + (selectedFile ? 2 : 1)}. ${nodeType}: ${result.filename}${nodeInfo}`,
            nodeType: nodeType
          };
          
          if (result.data) {
            imageInfo.image = `data:image/png;base64,${result.data}`;
            imageInfo.hasData = true;
          } else {
            imageInfo.image = null;
            imageInfo.hasData = false;
            imageInfo.placeholder = `No image data available for ${result.filename}`;
          }
          
          debugImageData.push(imageInfo);
        });
        
        // Extract prompt information from the workflow
        let promptText = '';
        
        // Find nodes with PROMPT meta tag
        for (const [nodeId, node] of Object.entries(workflow)) {
          if (node._meta && node._meta.title === 'PROMPT') {
            if (node.inputs) {
              // Look for various possible prompt fields
              const possiblePromptFields = ['text', 'prompt', 'positive', 'positive_prompt', 'negative', 'negative_prompt', 'prompt_additional', 'prompt_negative', 'prompt_start', 'prompt_end', 'seed', 'string'];
              
              for (const field of possiblePromptFields) {
                if (node.inputs[field] && typeof node.inputs[field] === 'string' && node.inputs[field].length > 5) {
                  promptText = node.inputs[field];
                  break;
                }
              }
              
              // Special handling for PreviewTextNode
              if (node.class_type === 'PreviewTextNode') {
                for (const [fieldName, fieldValue] of Object.entries(node.inputs)) {
                  if (fieldName.includes('PreviewTextNode') && typeof fieldValue === 'string' && fieldValue.length > 10) {
                    promptText = fieldValue;
                    break;
                  }
                }
              }
              
              // Special handling for AKRandomPortraitBaseCharacter node
              if (node.class_type === 'AKRandomPortraitBaseCharacter') {
                const additional = node.inputs.prompt_additional || '';
                const negative = node.inputs.prompt_negative || '';
                const start = node.inputs.prompt_start || '';
                const end = node.inputs.prompt_end || '';
                
                if (additional || negative || start || end) {
                  promptText = `Start: ${start}\nAdditional: ${additional}\nEnd: ${end}\nNegative: ${negative}`.trim();
                }
              }
            }
            break; // Found the PROMPT node, no need to continue
          }
        }
        
        setDebugImageData(debugImageData);
        setDebugPrompt(promptText);
        

        
        // Use INPUT and OUTPUT images from the debug available images for AB compare
        let beforeImage, afterImage;
        
        // Map workflow results to their corresponding nodes
        const { inputResult, outputResult } = mapResultsToNodes(workflow, results);
        
        // Use INPUT as before, OUTPUT as after
        beforeImage = inputResult || results[0]; // Fallback to first result if no INPUT found
        afterImage = outputResult || results[1] || results[0]; // Fallback to second result if no OUTPUT found
        

        
        // Set the images for comparison
        setBeforeImage(beforeImage);
        setAfterImage(afterImage);
        setBeforeAfterMode(true);
        
        setProgress('Images ready!');
        setProgressPercentage(99);
        
        addLog('Image compare enabled - showing before/after comparison');
        
        // Use the same afterImage that was determined for the comparison
        const historyItem = {
          before: beforeImage,
          after: afterImage, // Use the same afterImage that was set for comparison
          prompt: promptText, // Store the extracted prompt
          timestamp: new Date()
        };
        

        
        setImageHistory(prev => [...prev, historyItem]);
        setCurrentHistoryIndex(prev => prev + 1);
      } else {
        addLog('No results found');
      }
    } catch (error) {
      addLog(`Error fetching results: ${error.message}`);
    }
  };

  const updateGlobalProgress = () => {
    // Update global progress based on active workflows
    const runningWorkflows = activeWorkflows.filter(wf => wf.status === 'running');
    const completedWorkflows = activeWorkflows.filter(wf => wf.status === 'complete');
    const failedWorkflows = activeWorkflows.filter(wf => wf.status === 'failed');
    
    if (runningWorkflows.length > 0) {
      setIsRunning(true);
      setProgress(`Running ${runningWorkflows.length} workflow(s)...`);
      // Calculate average progress
      const avgProgress = runningWorkflows.reduce((sum, wf) => sum + (wf.progressPercentage || 0), 0) / runningWorkflows.length;
      setProgressPercentage(avgProgress);
    } else if (activeWorkflows.length > 0) {
      setIsRunning(false);
      setProgress(`Completed ${completedWorkflows.length}, Failed ${failedWorkflows.length}`);
      setProgressPercentage(100);
    } else {
      setIsRunning(false);
      setProgress('Ready');
      setProgressPercentage(0);
    }
  };

  const fetchResultsForWorkflow = async (executionId, promptId) => {
    try {
      setProgress('Loading results...');
      setProgressPercentage(95);
      
      const response = await fetchWorkflowResults(promptId);
      
      console.log('=== FETCH RESULTS FOR WORKFLOW DEBUG ===');
      console.log('Response:', response);
      
      // Handle the new response format: { images, outputs }
      const results = response.images || response;
      
      if (results && results.length > 0) {
        setProgress('Processing images...');
        setProgressPercentage(98);
        
        // For now, just update the global results with the latest workflow results
        // In a more sophisticated implementation, you might want to track results per workflow
        setResults(results);
        
        // Map workflow results to their corresponding nodes
        const { inputResult, outputResult } = mapResultsToNodes(workflow, results);
        
        // Set the images for comparison
        setBeforeImage(inputResult || results[0]);
        setAfterImage(outputResult || results[1] || results[0]);
        setBeforeAfterMode(true);
        
        setProgress('Images ready!');
        setProgressPercentage(99);
        
        addLog(`Workflow ${executionId} results loaded`);
        
        // Extract prompt information from the workflow
        let promptText = '';
        
        // Find nodes with PROMPT meta tag
        for (const [nodeId, node] of Object.entries(workflow)) {
          if (node._meta && node._meta.title === 'PROMPT') {
            if (node.inputs) {
              // Look for various possible prompt fields
              const possiblePromptFields = ['text', 'prompt', 'positive', 'positive_prompt', 'negative', 'negative_prompt', 'prompt_additional', 'prompt_negative', 'prompt_start', 'prompt_end', 'seed', 'string'];
              
              for (const field of possiblePromptFields) {
                if (node.inputs[field] && typeof node.inputs[field] === 'string' && node.inputs[field].length > 5) {
                  promptText = node.inputs[field];
                  break;
                }
              }
              
              // Special handling for PreviewTextNode
              if (node.class_type === 'PreviewTextNode') {
                for (const [fieldName, fieldValue] of Object.entries(node.inputs)) {
                  if (fieldName.includes('PreviewTextNode') && typeof fieldValue === 'string' && fieldValue.length > 10) {
                    promptText = fieldValue;
                    break;
                  }
                }
              }
              
              // Special handling for AKRandomPortraitBaseCharacter node
              if (node.class_type === 'AKRandomPortraitBaseCharacter') {
                const additional = node.inputs.prompt_additional || '';
                const negative = node.inputs.prompt_negative || '';
                const start = node.inputs.prompt_start || '';
                const end = node.inputs.prompt_end || '';
                
                if (additional || negative || start || end) {
                  promptText = `Start: ${start}\nAdditional: ${additional}\nEnd: ${end}\nNegative: ${negative}`.trim();
                }
              }
            }
            break; // Found the PROMPT node, no need to continue
          }
        }
        
        // Add to session history
        const historyItem = {
          before: inputResult || results[0],
          after: outputResult || results[1] || results[0],
          prompt: promptText,
          timestamp: new Date()
        };
        
        setImageHistory(prev => [...prev, historyItem]);
        setCurrentHistoryIndex(prev => prev + 1);
        
        addLog('Image compare enabled - showing before/after comparison');
      } else {
        addLog(`No results found for workflow ${executionId}`);
      }
    } catch (error) {
      addLog(`Error fetching results for workflow ${executionId}: ${error.message}`);
    }
  };

  const clearWorkflow = () => {
    // Don't clear the workflow - just clear the results and images
    // setWorkflow(null); // Keep the workflow selected
    // setSelectedWorkflow(''); // Keep the workflow selected
    setSelectedFile(null); // Clear the uploaded image
    setResults([]);
    setBeforeAfterMode(false);
    setBeforeImage(null);
    setAfterImage(null);
    setProgress('Ready');
    setProgressPercentage(0);
    setExecutionProgress({
      currentStep: 0,
      totalSteps: 0,
      currentNode: null,
      rate: null,
      percentage: 0
    });
    // Clear active workflows
    setActiveWorkflows([]);
    addLog('Workflow cleared');
  };

  const clearHistory = () => {
    setImageHistory([]);
    setCurrentHistoryIndex(-1);
    addLog('History cleared');
  };

  const removeHistoryItem = (indexToRemove) => {
    const actualIndex = imageHistory.length - 1 - indexToRemove; // Convert from reverse index to actual index
    setImageHistory(prev => prev.filter((_, index) => index !== actualIndex));
    setCurrentHistoryIndex(prev => {
      if (prev >= actualIndex) {
        return Math.max(0, prev - 1);
      }
      return prev;
    });
    addLog(`Removed history item ${actualIndex + 1}`);
  };

  // Function to map workflow results to their corresponding nodes based on meta tags
  const mapResultsToNodes = (workflow, results) => {
    console.log('=== WORKFLOW STRUCTURE ANALYSIS ===');
    Object.entries(workflow).forEach(([nodeId, node]) => {
      console.log(`Node ${nodeId}:`, {
        class_type: node.class_type,
        meta: node._meta,
        inputs: node.inputs,
        outputs: node.outputs || 'No outputs defined'
      });
    });
    
    // Find nodes with specific meta tags
    const loadNodes = [];
    const inputNodes = [];
    const outputNodes = [];
    
    // Categorize nodes by their meta tags
    Object.entries(workflow).forEach(([nodeId, node]) => {
      if (node._meta && node._meta.title) {
        switch (node._meta.title) {
          case 'LOAD':
            loadNodes.push({ nodeId, node });
            break;
          case 'INPUT':
            inputNodes.push({ nodeId, node });
            break;
          case 'OUTPUT':
            outputNodes.push({ nodeId, node });
            break;
        }
      }
    });
    
    console.log('Found nodes:', {
      load: loadNodes.map(n => n.nodeId),
      input: inputNodes.map(n => n.nodeId),
      output: outputNodes.map(n => n.nodeId)
    });
    
    // NEW: Use nodeId field for direct matching
    let inputResult = null;
    let outputResult = null;
    
    // Map results to nodes using the nodeId field
    results.forEach((result, index) => {
      console.log(`Result ${index}: nodeId=${result.nodeId}, filename=${result.filename}`);
      
      // Check if this result comes from an INPUT node
      if (result.nodeId && inputNodes.some(inputNode => inputNode.nodeId === result.nodeId)) {
        inputResult = result;
        console.log(`Found INPUT result by nodeId: ${result.filename} (nodeId: ${result.nodeId})`);
      }
      
      // Check if this result comes from an OUTPUT node
      if (result.nodeId && outputNodes.some(outputNode => outputNode.nodeId === result.nodeId)) {
        outputResult = result;
        console.log(`Found OUTPUT result by nodeId: ${result.filename} (nodeId: ${result.nodeId})`);
      }
    });
    
    // Fallback: If we couldn't find by nodeId, try execution order mapping
    if (!inputResult && inputNodes.length > 0) {
      console.log('Falling back to execution order mapping for INPUT...');
      const inputNodeId = inputNodes[0].nodeId;
      const nodeIds = Object.keys(workflow);
      const inputNodeIndex = nodeIds.indexOf(inputNodeId);
      
      if (inputNodeIndex >= 0 && inputNodeIndex < results.length) {
        inputResult = results[inputNodeIndex];
        console.log(`Mapped INPUT result by execution order: ${inputResult.filename}`);
      }
    }
    
    if (!outputResult && outputNodes.length > 0) {
      console.log('Falling back to execution order mapping for OUTPUT...');
      const outputNodeId = outputNodes[0].nodeId;
      const nodeIds = Object.keys(workflow);
      const outputNodeIndex = nodeIds.indexOf(outputNodeId);
      
      if (outputNodeIndex >= 0 && outputNodeIndex < results.length) {
        outputResult = results[outputNodeIndex];
        console.log(`Mapped OUTPUT result by execution order: ${outputResult.filename}`);
      } else if (results.length > 0) {
        // Use last result as fallback
        outputResult = results[results.length - 1];
        console.log(`Using last result as OUTPUT fallback: ${outputResult.filename}`);
      }
    }
    
    // Final fallback: Try filename patterns
    if (!inputResult && inputNodes.length > 0) {
      console.log('Trying filename pattern matching for INPUT...');
      inputResult = results.find(result => 
        result.filename && (
          result.filename.includes('INPUT') ||
          result.filename.includes('input') ||
          result.filename.includes('before')
        )
      );
      
      // Method 2: Look for the result that corresponds to the INPUT node's class type
      if (!inputResult) {
        const inputNode = inputNodes[0];
        console.log('INPUT node class type:', inputNode.node.class_type);
        
        // Try to find a result that might correspond to this node type
        // This is a heuristic - we'll look for patterns in the filename
        inputResult = results.find(result => {
          const filename = result.filename.toLowerCase();
          const classType = inputNode.node.class_type.toLowerCase();
          
          // Look for common patterns
          if (classType.includes('load') && filename.includes('temp')) {
            return true;
          }
          if (classType.includes('input') && filename.includes('temp')) {
            return true;
          }
          return false;
        });
      }
    }
    
    if (!outputResult && outputNodes.length > 0) {
      console.log('Trying alternative OUTPUT mapping...');
      
      // Method 1: Look for results that might be OUTPUT based on filename patterns
      outputResult = results.find(result => 
        result.filename && (
          result.filename.includes('OUTPUT') ||
          result.filename.includes('output') ||
          result.filename.includes('after') ||
          result.filename.includes('result')
        )
      );
      
      // Method 2: Look for the result that corresponds to the OUTPUT node's class type
      if (!outputResult) {
        const outputNode = outputNodes[0];
        console.log('OUTPUT node class type:', outputNode.node.class_type);
        
        // Try to find a result that might correspond to this node type
        outputResult = results.find(result => {
          const filename = result.filename.toLowerCase();
          const classType = outputNode.node.class_type.toLowerCase();
          
          // Look for common patterns
          if (classType.includes('save') && filename.includes('temp')) {
            return true;
          }
          if (classType.includes('output') && filename.includes('temp')) {
            return true;
          }
          return false;
        });
      }
    }
    
    console.log('Final mapping:', {
      inputResult: inputResult ? inputResult.filename : 'null',
      outputResult: outputResult ? outputResult.filename : 'null'
    });
    
    return { inputResult, outputResult };
  };









  const downloadImage = (imageUrl, filename) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAfterImage = () => {
    if (afterImage && afterImage.data) {
      const filename = `ITORA_gen_${downloadCounter}.png`;
      downloadImage(`data:image/png;base64,${afterImage.data}`, filename);
      setDownloadCounter(prev => prev + 1);
    }
  };

  // Parameter editor functions
  const handleNodeSelect = (nodeId) => {
    setSelectedNode(nodeId);
    setSelectedParameter(null);
    setParameterValue('');
  };

  const handleParameterSelect = (parameterName) => {
    setSelectedParameter(parameterName);
    if (workflow && selectedNode && workflow[selectedNode] && workflow[selectedNode].inputs) {
      setParameterValue(workflow[selectedNode].inputs[parameterName] || '');
    }
  };

  const handleParameterChange = (value) => {
    setParameterValue(value);
  };

  const handleParameterSave = () => {
    if (workflow && selectedNode && selectedParameter) {
      const updatedWorkflow = updateWorkflowParameter(workflow, selectedNode, selectedParameter, parameterValue);
      setWorkflow(updatedWorkflow);
      addLog(`Updated ${selectedNode}.${selectedParameter} = ${parameterValue}`);
    }
  };





  return (
    <div className="gen-container">
        <div className="gen-two-column">
        {/* Left Column - Image Area */}
          <div className="gen-left-column">
          <ImageArea
            selectedFile={selectedFile}
            beforeImage={beforeImage}
            afterImage={afterImage}
            beforeAfterMode={beforeAfterMode}
            results={results}
            isRunning={isRunning}
            imageHistory={imageHistory}
            onFileSelect={handleFileSelect}
            onRemoveImage={() => {
              setSelectedFile(null);
              setBeforeImage(null);
              addLog('Removed selected image');
            }}
            onDownloadImage={(image) => {
              const filename = `ITORA_gen_${downloadCounter}.png`;
              downloadImage(`data:image/png;base64,${image.data}`, filename);
              setDownloadCounter(prev => prev + 1);
            }}
            onDownloadAfterImage={downloadAfterImage}
            onFullscreenToggle={handleFullscreenToggle}

            onCropImage={() => {
              if (selectedFile) {
                setImageToCrop(selectedFile);
                setShowCropper(true);
              }
            }}
            isFullscreen={isFullscreen}
          />
          </div>

        {/* Right Column - Controls */}
          <div className="gen-right-column">
          <WorkflowControls
            serverStatus={serverStatus}
            availableWorkflows={availableWorkflows}
            selectedWorkflow={selectedWorkflow}
            selectedFile={selectedFile}
            isRunning={isRunning}
            progress={progress}
            progressPercentage={progressPercentage}
            executionProgress={executionProgress}
            hasWebSocketProgress={hasWebSocketProgress}
            wsConnected={wsConnected}
            wsConnectionStatus={wsConnectionStatus}
            queueInfo={queueInfo}
            onWorkflowSelect={handleWorkflowSelect}
            onRunWorkflow={runWorkflow}
            onClearWorkflow={clearWorkflow}
            onRequestQueueInfo={requestQueueInfo}
          />

          {/* Active Jobs Display */}
          {activeWorkflows.length > 0 && (
            <div className="gen-section compact">
              <div className="tool-header">
                <h3 onClick={() => setShowActiveJobs(!showActiveJobs)} style={{ cursor: 'pointer' }}>ACTIVE JOBS ({activeWorkflows.length})</h3>
                <button 
                  type="button"
                  className="tool-toggle" 
                  onClick={() => setShowActiveJobs(!showActiveJobs)}
                >
                  {showActiveJobs ? '−' : '+'}
                </button>
              </div>
              {showActiveJobs && (
                <div className="active-workflows-list">
                  {activeWorkflows.map((workflow) => (
                    <div key={workflow.id} className={`workflow-item ${workflow.status}`}>
                      <div className="workflow-name">{workflow.name.replace('.json', '')}</div>
                      <div className="workflow-status">{workflow.status}</div>
                      <div className="workflow-progress">{workflow.progress}</div>
                      {workflow.progressPercentage > 0 && (
                        <div className="workflow-progress-bar">
                          <div 
                            className="workflow-progress-fill" 
                            style={{ width: `${workflow.progressPercentage}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="gen-section compact">
            <div className="tool-header">
              <h3 onClick={() => setShowEditor(!showEditor)} style={{ cursor: 'pointer' }}>PARAMETERS</h3>
                  <button 
                    type="button"
                className="tool-toggle" 
                onClick={() => setShowEditor(!showEditor)}
              >
                {showEditor ? '−' : '+'}
                    </button>
            </div>
            {showEditor && (
              <ParameterEditor
                workflow={workflow}
                selectedNode={selectedNode}
                selectedParameter={selectedParameter}
                parameterValue={parameterValue}
                onNodeSelect={handleNodeSelect}
                onParameterSelect={handleParameterSelect}
                onParameterChange={handleParameterChange}
                onParameterSave={handleParameterSave}
              />
              )}
            </div>

          <div className="gen-section compact">
            <div className="tool-header">
              <h3 onClick={() => setShowLogPanel(!showLogPanel)} style={{ cursor: 'pointer' }}>LOG</h3>
                  <button 
                    type="button"
                className="tool-toggle" 
                onClick={() => setShowLogPanel(!showLogPanel)}
              >
                {showLogPanel ? '−' : '+'}
                    </button>
            </div>
            {showLogPanel && (
              <LogDisplay
                logs={logs}
                websocketLogs={websocketLogs}
                showLog={showLog}
                setShowLog={setShowLog}
                showWebsocketLog={showWebsocketLog}
                setShowWebsocketLog={setShowWebsocketLog}
              />
            )}
          </div>

          {/* Debug Images Panel */}
          {debugImageData.length > 0 && (
            <div className="gen-section compact">
              <div className="tool-header">
                <h3 onClick={() => setShowDebugPanel(!showDebugPanel)} style={{ cursor: 'pointer' }}>DEBUG: Available Images ({debugImageData.length})</h3>
                <button 
                  type="button"
                  className="tool-toggle" 
                  onClick={() => setShowDebugPanel(!showDebugPanel)}
                >
                  {showDebugPanel ? '−' : '+'}
                </button>
        </div>
              {showDebugPanel && (
                <div className="debug-images-panel">
                  {/* Detailed Debug Information */}
                  <div className="debug-info-section">
                    <h4>Detailed Result Information:</h4>
                    <div className="debug-info-list">
                      {console.log('=== DEBUG PANEL RENDER ===', 'debugImages:', debugImages, 'length:', debugImages.length)}
                      {debugImages.map((info, index) => (
                        <div key={index} className="debug-info-item">
                          {info}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Image Display */}
                  <div className="debug-images-section">
                    <h4>Image Display:</h4>
                    {debugImageData.map((imageData, index) => (
                      <div key={index} className="debug-image-item">
                        <div className="debug-image-label" data-nodetype={imageData.nodeType}>
                          {imageData.nodeType === 'uploaded' ? 'Uploaded File' : 
                           imageData.nodeType === 'INPUT' ? 'INPUT Image' :
                           imageData.nodeType === 'OUTPUT' ? 'OUTPUT Image' : 
                           `Result ${index + 1}`}
                        </div>
                        <div className="debug-image-container">
                          {imageData.hasData ? (
                            <img 
                              src={imageData.image} 
                              alt={imageData.name}
                              className="debug-image"
                            />
                          ) : (
                            <div className="debug-image-placeholder">
                              {imageData.placeholder}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Prompt Display */}
                  {debugPrompt && (
                    <div className="debug-prompt-section">
                      <div className="debug-prompt-label">Prompt:</div>
                      <div className="debug-prompt-text">{debugPrompt}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image History Section - Separate Container */}
      {imageHistory && imageHistory.length > 0 && (
        <div className="gen-history-container">
          <div className="history-header">
            <h4>Session History</h4>
                          <div className="history-header-controls">
                <span className="history-count">{imageHistory.length} generated</span>
                <button 
                  onClick={handleFullscreenToggle}
                  className="fullscreen-btn"
                  title="Toggle fullscreen"
                >
                  ⛶
                </button>
                <button 
                  onClick={clearHistory}
                  className="clear-history-btn"
                  title="Clear history"
                >
                  ✕
                </button>
              </div>
          </div>
          <div className="history-grid">
            {imageHistory.slice().reverse().map((item, index) => (
              <div 
                key={index} 
                className="history-item"
                onClick={() => {
                  if (item.after) {
                    setBeforeImage(item.before);
                    setAfterImage(item.after);
                    setBeforeAfterMode(true);
                    addLog(`Swapped to history item ${imageHistory.length - index}`);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="history-image-container">
                  {item.after && (
                    <img 
                      src={`data:image/png;base64,${item.after.data}`} 
                      alt={item.prompt ? `Generated result - Prompt: ${item.prompt}` : "Generated result"} 
                      className="history-image"
                    />
                  )}
                </div>
                <div className="history-timestamp">
                  {item.timestamp && new Date(item.timestamp).toLocaleTimeString()}
                </div>
                <div className="history-controls">
                  {item.after && (
                    <>
                      <button 
                        onClick={() => {
                          const filename = `ITORA_gen_${downloadCounter}.png`;
                          downloadImage(`data:image/png;base64,${item.after.data}`, filename);
                          setDownloadCounter(prev => prev + 1);
                        }}
                        className="download-btn small"
                        title="Download result"
                      >
                        ↓
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the item click
                          removeHistoryItem(index);
                        }}
                        className="remove-btn small"
                        title="Remove from history"
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <ImageCropper
          image={imageToCrop}
          onCrop={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};

export default Gen;
